/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { appConstants } from '@app/common/app-constants';
import { MicroAppBase } from './micro-app-base';
import { ModalsService } from '@app/common/modals/services/modals.service';
import { PostMessengerService } from '@app/di/channel/window/post-messenger.service';
import { Logger } from '@core/lib/services';
import { ModalRef } from '@core/public-api';

const log = new Logger('ModalMicroApp');

/**
 * Modal implementation of a micro app
 * Handles modal-specific functionality for micro applications
 * @template T The type of the status data
 */
export class ModalMicroApp<T = any> extends MicroAppBase<T> {
  private modalRef: ModalRef | null = null;
  closeStatusSyncInterval: any = null;
  constructor(
    appID: string,
    url: URL,
    sessionID: string,
    private readonly allowLocalNetworkResource: boolean = true,
    postMessageService: PostMessengerService,
    private modalService: ModalsService,
    private onClosed: (appId: string) => void,
    private onMinimizedChanged: () => void
  ) {
    super('Modal', appID, url, sessionID, postMessageService);
  }

  async launch(): Promise<void> {
    const data = {
      appName: this.appID,
      url: this.url.toString(),
      sessionID: this.sessionID,
      loadTimeout: appConstants.TIME.SS_TEN,
      allowLocalNetworkResource: this.allowLocalNetworkResource
    };

    this.modalRef = await this.modalService.openMicroAppModal(data);
    this._minimized = false;

    // Handle outside clicks to minimize
    this.modalRef.afterOutsideClicked.subscribe(() => {
      this.minimize();
    });
  }

  restore(): void {
    if (this.modalRef) {
      this.modalRef.setVisibility(true);
      this._minimized = false;
      this.onMinimizedChanged();
    }
  }

  minimize(): void {
    if (this.modalRef) {
      this.modalRef.setVisibility(false);
      this._minimized = true;
      this.onMinimizedChanged();
    }
  }

  updateStatus(status: T): void {
    this._status.next(status);
    this.onMinimizedChanged();
  }

  close(): void {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
    if (this.closeStatusSyncInterval) {
      this.closeStatusSyncInterval();
      this.closeStatusSyncInterval = null;
    }
    this.cleanup();
    this.onClosed(this.appID);
  }

  isActive(): boolean {
    return this.modalRef !== null;
  }

  // Modal-specific method for registering iframe window
  registerWindow(appWindow: Window): boolean {
    try {
      this.postMessageService.register({
        targetAppId: this.appID,
        targetAppWindow: appWindow,
        targetOrigin: this.url.origin,
        onResponse: data => {
          this.updateStatus(data);
          this.onMinimizedChanged();
        }
      });
      return true;
    } catch (error) {
      log.error('Error registering window:', error);
      return false;
    }
  }

  // Modal-specific method for closing with keep status
  closeWithKeepStatus(): void {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
      this._minimized = true;
      this.onMinimizedChanged();
    }
  }
}
